import React from "react";
import { Box, CircularProgress, IconButton, List, ListItemButton, Tooltip, Typography } from "@mui/material";
import { BsArrowClockwise } from "react-icons/bs";

const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace";

const STATUS_COLORS = {
    completed: "#2e7d32",
    failed: "#d32f2f",
    cancelled: "#ed6c02",
    cancelling: "#ed6c02",
    running: "#0F4F96",
    queued: "#757575",
};

const describe = (item) => {
    const rows = Number(item.rowcount || 0).toLocaleString();
    if (item.status === "completed") return `${rows} rows`;
    if (item.status === "failed") return "Failed";
    if (item.status === "cancelled") return `Stopped after ${rows} rows`;
    if (item.status === "queued") return "Queued";
    return `Running · ${rows} rows`;
};

/**
 * Queries run before, newest first, so one can be picked up and adjusted rather
 * than retyped. Results themselves are never kept here - they only ever exist
 * as the encrypted file in the bucket.
 */
export default function HistoryList({ items, loading, onRefresh, onSelect }) {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1 }}>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    Your last {items.length} queries · click one to load it
                </Typography>
                <Tooltip title="Reload">
                    <span>
                        <IconButton size="small" onClick={onRefresh} disabled={loading}>
                            <BsArrowClockwise size={16} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                {loading && items.length === 0 && (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}
                {!loading && items.length === 0 && (
                    <Typography sx={{ p: 2, fontSize: 13, color: "text.secondary" }}>
                        No queries yet.
                    </Typography>
                )}
                <List dense disablePadding>
                    {items.map((item) => (
                        <ListItemButton
                            key={item.id}
                            onClick={() => onSelect(item)}
                            title={item.errormessage || undefined}
                            sx={{ display: "block", borderBottom: "1px solid #eee", py: 1 }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                <Box
                                    sx={{
                                        width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                                        backgroundColor: STATUS_COLORS[item.status] || "#9e9e9e",
                                    }}
                                />
                                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                                    {item.queuedat} IST · {describe(item)}
                                </Typography>
                            </Box>
                            {/* The name the user gave it reads far better than
                                three lines of SQL; the SQL stays underneath. */}
                            {item.exportname && (
                                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.25 }}>
                                    {item.exportname}
                                </Typography>
                            )}
                            <Typography
                                sx={{
                                    fontSize: 12, fontFamily: MONO,
                                    color: item.exportname ? "text.secondary" : "text.primary",
                                    whiteSpace: "pre-wrap", wordBreak: "break-word",
                                    display: "-webkit-box",
                                    WebkitLineClamp: item.exportname ? 2 : 3,
                                    WebkitBoxOrient: "vertical", overflow: "hidden",
                                }}
                            >
                                {item.querytext}
                            </Typography>
                        </ListItemButton>
                    ))}
                </List>
            </Box>
        </Box>
    );
}
